import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Globe, ChevronRight, Search, Plus, Gauge } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { StatusPill, EmptyState, type StatusTone } from '@vnetwork/ui/data';
import { Badge, Button, Input, Skeleton } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useCdnUmbrellas, type CdnUmbrella } from '@/api/manticore/cdn-providers';
import { cdnProviderLabel } from '../cdn-provider-display';
import { useMyProjects } from '@/api/projects';
import { PRODUCT_GROUP } from '@/lib/lead-product-group';
import { useSelfServeActivation } from '../activation-mode';
import { ServiceContactSalesDialog } from '../components/ServiceContactSalesDialog';
import { CdnActivateDialog } from './CdnActivateDialog';
import styles from './CdnHome.module.css';

const BASE = '/services/cdn';

function umbrellaStatusTone(status: string): StatusTone {
  const s = status.toLowerCase();
  if (s === 'active') return 'success';
  if (s === 'suspended' || s === 'degraded') return 'warning';
  return 'muted';
}

// ---------------------------------------------------------------------------
// Single umbrella card — a read-only directory entry. Project assignment is
// managed inside each orchestrator's console, not from this list.
// ---------------------------------------------------------------------------

function UmbrellaCard({ umbrella, projectName }: { umbrella: CdnUmbrella; projectName?: string }) {
  const providers = umbrella.providers ?? [];
  return (
    <Link to={`${BASE}/${umbrella.service_id}`} className={styles.card}>
      <div className={styles.head}>
        <span className={styles.icon}>
          <Globe size={19} />
        </span>
        <div className={styles.titleWrap}>
          <div className={styles.title}>{umbrella.service_name}</div>
          <div className={styles.sub}>{cdnProviderLabel(umbrella.provider_name)}</div>
        </div>
        {umbrella.is_shared && (
          // No owner tooltip: raw org ids must never surface (white-label).
          <Badge tone="info" appearance="subtle" size="sm">
            Shared
          </Badge>
        )}
        <StatusPill tone={umbrellaStatusTone(umbrella.status)}>
          {umbrella.status || 'Unknown'}
        </StatusPill>
      </div>

      <div className={styles.foot}>
        <span className={styles.providerRow}>
          {providers.length === 0 ? (
            <span className={styles.sub}>Provider: {cdnProviderLabel(umbrella.provider_name)}</span>
          ) : (
            providers.slice(0, 3).map((p) => (
              <Badge
                key={p.provider_name}
                tone={p.is_canonical ? 'info' : 'neutral'}
                appearance="subtle"
                size="sm"
              >
                {cdnProviderLabel(p.provider_name)}
                {p.is_canonical ? ' · primary' : ''}
              </Badge>
            ))
          )}
          {providers.length > 3 && (
            <span className={styles.sub}>+{providers.length - 3}</span>
          )}
          {projectName && (
            <Badge tone="neutral" appearance="subtle" size="sm">
              {projectName}
            </Badge>
          )}
        </span>
        <span className={styles.openHint}>
          Open <ChevronRight size={14} />
        </span>
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// CDN Home — a flat, searchable directory of the customer's orchestrators.
// ---------------------------------------------------------------------------

export function CdnHomePage() {
  const [search, setSearch] = useState('');
  const [activateOpen, setActivateOpen] = useState(false);
  // Self-serve provisioning is gated (contact-sales for direct customers, on
  // while staff impersonate); when off the CTA opens a contact-sales dialog.
  const [contactOpen, setContactOpen] = useState(false);
  const selfServeActivation = useSelfServeActivation();
  const openActivate = () =>
    selfServeActivation ? setActivateOpen(true) : setContactOpen(true);

  const { data, isLoading, isError, refetch } = useCdnUmbrellas();
  const umbrellas = data?.umbrellas ?? [];

  // Project names resolve the read-only chip shown on assigned orchestrators.
  const { data: projects = [] } = useMyProjects();
  const projectNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of projects) m.set(p.id, p.name);
    return m;
  }, [projects]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matched = !q
      ? umbrellas
      : umbrellas.filter(
          (u) =>
            u.service_name.toLowerCase().includes(q) ||
            u.scope.toLowerCase().includes(q) ||
            u.provider_name.toLowerCase().includes(q),
        );
    // Active orchestrators first, then alphabetically — keeps the working set
    // on top for orgs holding many umbrellas.
    return [...matched].sort((a, b) => {
      const aActive = a.status.toLowerCase() === 'active' ? 0 : 1;
      const bActive = b.status.toLowerCase() === 'active' ? 0 : 1;
      return aActive - bActive || a.service_name.localeCompare(b.service_name);
    });
  }, [umbrellas, search]);

  // Umbrellas shared in from another org get their own section — visible but
  // never mixed in with the org's own fleet.
  const owned = useMemo(() => filtered.filter((u) => !u.is_shared), [filtered]);
  const shared = useMemo(() => filtered.filter((u) => u.is_shared), [filtered]);

  return (
    <Container size="xl">
      <PageHeader
        icon={<Globe size={22} />}
        eyebrow="Services"
        title="Multi-CDN Orchestration"
        description="Select an orchestrator to manage its domains, analytics and provider backends."
        actions={
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
            {/* Org-wide usage across every orchestrator (Org → Services → Projects). */}
            <Button variant="secondary" startIcon={<Gauge size={15} />} asChild>
              <Link to={`${BASE}/usage`}>Business Usage</Link>
            </Button>
            {/* Self-serve activation is one orchestrator per org, so the CTA only
                shows when the org has none of its own yet (a second one 409s). */}
            {!isLoading && !isError && owned.length === 0 && (
              <Button startIcon={<Plus size={15} />} onClick={openActivate}>
                Activate Multi-CDN
              </Button>
            )}
          </div>
        }
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Couldn't load CDN orchestrators"
          actions={
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          The service catalog is temporarily unavailable. Please try again.
        </Alert>
      ) : isLoading ? (
        <>
          <div className={styles.searchBar}>
            <Skeleton style={{ height: 34, width: '100%', maxWidth: 360 }} />
          </div>
          <div className={styles.grid}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 116 }} />
            ))}
          </div>
        </>
      ) : umbrellas.length === 0 ? (
        <EmptyState
          icon={<Globe size={28} />}
          title="No CDN orchestrators"
          description="You don't have any Multi-CDN Orchestration services yet. Activate one to start delivering your domains across CDN backends."
          actions={
            <Button startIcon={<Plus size={15} />} onClick={openActivate}>
              Activate Multi-CDN
            </Button>
          }
        />
      ) : (
        <>
          {umbrellas.length > 2 && (
            <Toolbar
              start={
                <Input
                  inputSize="sm"
                  type="search"
                  placeholder="Search orchestrators…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search CDN orchestrators"
                  wrapperClassName={styles.searchWrap}
                  startSlot={<Search size={15} />}
                />
              }
            />
          )}

          {filtered.length === 0 ? (
            <EmptyState
              icon={<Globe size={26} />}
              title="No results"
              description={`No orchestrators match "${search}".`}
            />
          ) : (
            <>
              <div className={styles.grid}>
                {owned.map((u) => (
                  <UmbrellaCard
                    key={u.service_id}
                    umbrella={u}
                    projectName={u.project_id ? projectNameMap.get(u.project_id) : undefined}
                  />
                ))}
              </div>
              {shared.length > 0 && (
                <>
                  <h2 className={styles.sectionTitle}>Shared with you</h2>
                  <div className={styles.grid}>
                    {shared.map((u) => (
                      <UmbrellaCard
                        key={u.service_id}
                        umbrella={u}
                        projectName={u.project_id ? projectNameMap.get(u.project_id) : undefined}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      <CdnActivateDialog open={activateOpen} onOpenChange={setActivateOpen} />
      <ServiceContactSalesDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        serviceLabel="Multi-CDN Orchestration"
        productGroup={PRODUCT_GROUP.cdn}
      />
    </Container>
  );
}
